<template>
  <div :class="['search', { active, disabled, 'menu-open': showMenu, global, header }]">
    <dropdown
      flex
      :active="active"
      @close="handleClose"
    >
      <div class="input" @click.stop.prevent="tryActivate">
        <icon name="search" class="search-icon" />

        <input
          ref="input"
          v-model="query"
          type="search"
          maxlength="10000"
          :placeholder="placeholder"
          @focus.stop.prevent="tryActivate"
          @keyup.up.prevent="handleUp"
          @keyup.down.prevent="handleDown"
          @keyup.enter.prevent="handleEnter"
        />
      </div>

      <template #menu>
        <div class="dropdown-menu">
          <div v-if="global" v-show="!showRecentGlobalSearches">
            <slot name="header" />
          </div>

          <div v-show="searching && items.length === 0" class="loading-container">
            <spinner inline />
          </div>

          <div v-show="showRecentGlobalSearches" class="recent-searches">
            <div
              v-for="(recentSearch, i) in recentGlobalSearches"
              :key="i"
              class="item"
              @click="handleSelectRecentSearch(recentSearch)"
            >
              <align small-gap justify="space-between" class="recent-search">
                <span>{{ recentSearch }}</span>

                <icon-button size="18" icon="close" @click.stop="deleteRecentGlobalSearch(i)" />
              </align>
            </div>
          </div>

          <div v-show="showNoResults" class="no-results">
            <span>No results</span>
          </div>

          <div
            v-for="(item, i) in options"
            ref="items"
            :key="item.id"
            :class="['item', { highlight: i === arrowCounter }]"
            @click.stop.prevent="handleSelect(item)"
            @mouseover.prevent="arrowCounter = -1"
          >
            <slot :item="item" :source="source">
              {{ item.label }}
            </slot>
          </div>
        </div>
      </template>
    </dropdown>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash.debounce';
import * as Sentry from '@sentry/vue';

import { authService, searchService } from '@/services';

export default {
  props: {
    disabled: Boolean,
    global: Boolean,
    header: Boolean,
    excludeIds: Array,
    source: String,
    sort: String,
    includeHidden: Boolean,
    keepOpenOnSelect: Boolean,
    placeholder: {
      type: String,
      default: 'Search',
    },
  },

  data() {
    return {
      query: '',
      active: false,
      items: [],
      searching: false,
      arrowCounter: -1,
    };
  },

  created() {
    this.search = debounce(this.search, 500);
    this.saveRecentSearch = debounce(this.saveRecentSearch, 2000);
  },

  watch: {
    query(value) {
      if (value) {
        this.searching = true;
        this.search();
        this.saveRecentSearch();
      } else {
        this.searching = false;
        this.items = [];
      }
    },

    source() {
      this.searching = true;
      this.items = [];
      this.search();
    },

    active(value) {
      if (value) {
        this.$nextTick(() => {
          this.fixScrolling();
        });
      }
    },
  },

  computed: {
    ...mapState({
      userId: (state) => state.user.uid,
      recentSearchesObj: (state) => state.user.recentSearches || {},
    }),

    recentGlobalSearches() {
      return this.recentSearchesObj?.global || [];
    },

    options() {
      return this.items
        .filter(({ id }) => !this.excludeIds || this.excludeIds.indexOf(id) === -1);
    },

    showNoResults() {
      return this.query.length && !this.searching && this.items.length === 0;
    },

    showRecentGlobalSearches() {
      return this.global && this.query === '' && !this.searching;
    },

    showMenu() {
      return this.active && (this.showNoResults || this.options.length);
    },
  },

  methods: {
    async search() {
      if (this.query.trim() === '') {
        return;
      }

      try {
        this.searching = true;

        const filters = {};

        if (this.source === 'profiles') {
          filters.hasProfileRedirect = false;
        }

        const data = await searchService.search({
          index: this.source,
          query: this.query,
          includeHidden: this.includeHidden,
          limit: 50,
          sort: this.sort,
          filters,
        });

        this.items = data.hits;

        this.arrowCounter = -1;
      } catch (e) {
        this.$toasted.error('Error searching');
        Sentry.captureException(e);
      }

      this.searching = false;
    },

    handleSelect(item) {
      if (item && item.id) {
        this.$emit('select', item);

        if (!this.keepOpenOnSelect) {
          this.active = false;

          if (this.$refs.input) {
            this.$refs.input.blur();
          }
        }
      }
    },

    handleSelectRecentSearch(recentSearch) {
      this.query = recentSearch;
    },

    handleClose() {
      this.active = false;
    },

    focus() {
      setTimeout(() => {
        this.$refs.input.focus();
      }, 500);
    },

    async saveRecentSearch() {
      if (this.global && this.query !== '') {
        try {
          await authService.updateUser(this.userId, {
            recentSearches: {
              ...this.recentSearchesObj,
              global: [...new Set([this.query.trim(), ...this.recentGlobalSearches])].slice(0, 10),
            },
          });
        } catch (e) {
          Sentry.captureException(e);
        }
      }
    },

    async deleteRecentGlobalSearch(i) {
      const global = [...this.recentGlobalSearches];

      global.splice(i, 1);

      try {
        await authService.updateUser(this.userId, {
          recentSearches: {
            ...this.recentSearchesObj,
            global,
          },
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    },

    input(value) {
      this.query = value;
    },

    tryActivate() {
      this.active = true;
    },

    handleUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter -= 1;
        this.fixScrolling();
      }
    },

    handleDown() {
      if (this.arrowCounter < this.options.length - 1) {
        this.arrowCounter += 1;
        this.fixScrolling();
      }
    },

    handleEnter() {
      if (this.arrowCounter < 0) {
        this.emitEnter();

        return;
      }

      const focusedItem = this.options[this.arrowCounter];

      if (focusedItem) {
        this.handleSelect(focusedItem);
      } else {
        this.emitEnter();
      }
    },

    fixScrolling() {
      if (this.arrowCounter < 0) {
        return;
      }

      const element = this.$refs.items[this.arrowCounter];

      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    },

    emitEnter() {
      if (this.query) {
        this.$emit('enter', this.query);
      }

      setTimeout(() => {
        this.active = false;

        if (this.$refs.input) {
          this.$refs.input.blur();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.search {
  position: relative;
  width: 100%;

  .dropdown {
    width: 100%;
    --dropdown-menu-height: 500px;
  }

  &.active {
    box-shadow: $elevation-1;
    border-radius: $border-radius;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    background-color: var(--theme-card);

    .input {
      background-color: var(--theme-card);
      border-bottom-color: transparent;
      border-color: transparent;
    }

    .search-icon {
      color: $color-green-1;
      transform: rotate(90deg);
    }

    @media($medium-down) {
      background-color: transparent;

      .input {
        background-color: transparent;
      }
    }
  }

  &.menu-open {
    .input {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &.disabled {
    pointer-events: none;

    .input,
    input {
      opacity: 0.6;
    }
  }

  &.header {
    &:not(.active) .input {
      background-color: var(--theme-highlight);
      border: none;
    }

    @media($medium-down) {
      box-shadow: none;

      .dropdown-menu {
        border-radius: 0;
        position: fixed;
        top: 56px;
        bottom: 0;
        left: 0;
        max-height: 100%;
      }

      .input {
        border: none;
        border-radius: 0;
      }
    }
  }
}

.input {
  background-color: var(--theme-input);
  border: solid 1px var(--theme-input-border);
  border-radius: $border-radius;
  width: 100%;
  padding: 0 0 0 $gp * .5;
  display: flex;
  align-items: center;
  position: relative;
}

input {
  border: none;
  outline: 0;
  padding: 12px 10px;
  flex: 1;
  border-radius: $border-radius;
  font-size: 18px;
  background-color: transparent;
}

.search-icon {
  @include transition(transform);
  color: $color-gray-5;
}

.no-results {
  padding: $gp;
}

.dropdown-menu {
  width: 100%;
  box-shadow: $elevation-1;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.item {
  background-color: var(--theme-card);

  &:not(:last-child) {
    border-bottom: 1px solid var(--theme-border);
  }

  &:hover,
  &.highlight {
    background-color: var(--theme-highlight);
  }

  &:active {
    background-color: var(--theme-active);
  }
}

.recent-search {
  @include ellipsis;
  padding: $gp * .5 $gp;
  color: $color-gray-5;
  --theme-icon-button-color: #{$color-gray-5};

  span {
    @include ellipsis;
  }
}

.loading-container {
  padding: $gp * .5;
}
</style>
